import { useCallback, useEffect, useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import { getSolanaConnection } from '../solana/connection';

/**
 * Fetch token balance for a specific mint and wallet
 * 
 * @param {string} walletAddress - Wallet public key string
 * @param {string} mintAddress - Token mint public key string
 * @returns {Promise<object>} - Balance information
 */
async function fetchMintBalance(walletAddress, mintAddress) {
  if (!walletAddress || !mintAddress) {
    return {
      balance: '0',
      balanceAtomic: '0',
    };
  }

  try {
    const connection = getSolanaConnection();
    const walletPubkey = new PublicKey(walletAddress);
    const mintPubkey = new PublicKey(mintAddress);

    // Get token accounts for this wallet and mint
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(walletPubkey, {
      mint: mintPubkey,
    });

    if (tokenAccounts.value.length === 0) {
      return {
        balance: '0',
        balanceAtomic: '0',
      };
    }

    // Sum up all balances (in case there are multiple ATAs)
    // eslint-disable-next-line no-undef
    const totalBalance = tokenAccounts.value.reduce((sum, account) => {
      const amount = account.account.data.parsed.info.tokenAmount.amount;
      // eslint-disable-next-line no-undef
      return sum + BigInt(amount);
      // eslint-disable-next-line no-undef
    }, BigInt(0));

    return {
      balance: totalBalance.toString(),
      balanceAtomic: totalBalance.toString(),
    };
  } catch (err) {
    console.error(`Error fetching balance for mint ${mintAddress}:`, err);
    return {
      balance: '0',
      balanceAtomic: '0',
    };
  }
}

/**
 * Hook to fetch wallet balances for multiple mints
 * 
 * @param {string} walletAddress - Connected wallet address
 * @param {Array<string>} mintAddresses - Array of mint addresses to check
 * @returns {object} - Balances map, loading state, and refresh function
 */
export function useWalletMintBalances(walletAddress, mintAddresses = []) {
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create stable string representation of mint addresses for dependency comparison
  const mintAddressesKey = JSON.stringify(mintAddresses.sort());

  const loadBalances = useCallback(async () => {
    if (!walletAddress || mintAddresses.length === 0) {
      // Only clear balances if wallet is disconnected, not just empty array
      if (!walletAddress) {
        setBalances({});
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const balancePromises = mintAddresses.map(async (mint) => {
        const balance = await fetchMintBalance(walletAddress, mint);
        return { mint, balance: balance.balance };
      });

      const results = await Promise.all(balancePromises);

      const balancesMap = results.reduce((acc, { mint, balance }) => {
        acc[mint] = balance;
        return acc;
      }, {});

      setBalances(balancesMap);
    } catch (err) {
      console.error('Error loading mint balances:', err);
      setError(err.message || 'Failed to load balances');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, mintAddressesKey]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  return {
    balances,
    loading,
    error,
    refresh: loadBalances,
  };
}

/**
 * Hook to get balance for a single mint
 * 
 * @param {string} walletAddress - Connected wallet address
 * @param {string} mintAddress - Single mint address to check
 * @returns {object} - Balance, loading state, and refresh function
 */
export function useSingleMintBalance(walletAddress, mintAddress) {
  const [balance, setBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBalance = useCallback(async () => {
    if (!walletAddress || !mintAddress) {
      setBalance('0');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchMintBalance(walletAddress, mintAddress);
      setBalance(result.balance);
    } catch (err) {
      console.error('Error loading mint balance:', err);
      setError(err.message || 'Failed to load balance');
    } finally {
      setLoading(false);
    }
  }, [walletAddress, mintAddress]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  return {
    balance,
    loading,
    error,
    refresh: loadBalance,
  };
}
